const TASK_INBOX_TYPES = new Set(['task_assigned', 'task_denied']);

/**
 * The chat notification types written by notify_chat_message().
 *
 * chat_mention is the same row as event_message/department_message, relabelled when the
 * message named you -- it still carries data.chat_key, so it deep-links identically. This
 * is not cosmetic: usePushNotifications builds a tapped push's destination from
 * data.type through getNotificationNavigationUrl, so leaving it out would make a mention
 * push tap to nowhere.
 */
const CHAT_TYPES = new Set([
  'event_message',
  'department_message',
  'direct_message',
  'chat_mention',
]);

function pickId(data: Record<string, unknown> | null | undefined, key: string): string | null {
  const raw = data?.[key];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * Communication deep link for a chat notification.
 *
 * `chat_key` is the thread identity shared with the database -- the same string
 * chatKey() builds -- and is written on every row by notify_chat_message(). Rows that
 * predate that trigger carry only `direct_chat_id`, so the key is rebuilt from it and
 * old notifications keep working.
 */
function chatUrl(n: { type: string; data?: Record<string, unknown> | null }): string | null {
  if (!CHAT_TYPES.has(n.type)) return null;

  const chatKey =
    pickId(n.data, 'chat_key') ??
    (pickId(n.data, 'direct_chat_id') ? `direct:${pickId(n.data, 'direct_chat_id')}` : null) ??
    (pickId(n.data, 'department_chat_id')
      ? `department:${pickId(n.data, 'department_chat_id')}`
      : null);

  if (!chatKey) return '/communication';

  const params = new URLSearchParams();
  // The page is scoped to one event, so a thread it is not showing cannot be selected.
  // Naming the event lets the deep link switch to it first.
  const eventId = pickId(n.data, 'event_id');
  if (eventId) params.set('event', eventId);
  params.set('chat', chatKey);
  return `/communication?${params.toString()}`;
}

function volunteerTasksUrl(n: { type: string; data?: Record<string, unknown> | null }): string | null {
  if (!TASK_INBOX_TYPES.has(n.type)) return null;

  const eventId = pickId(n.data, 'event_id');
  const taskId = pickId(n.data, 'task_id');

  if (taskId) {
    const params = new URLSearchParams({ task: taskId });
    if (eventId) params.set('event', eventId);
    return `/volunteer-tasks?${params.toString()}`;
  }

  if (eventId) return `/volunteer-tasks?event=${encodeURIComponent(eventId)}`;
  return null;
}

/** Deep-link from a notification row (chat, volunteer tasks, or org volunteers dashboard). */
export function getNotificationNavigationUrl(n: {
  type: string;
  data?: Record<string, unknown> | null;
}): string | null {
  const taskUrl = volunteerTasksUrl(n);
  if (taskUrl) return taskUrl;

  const chat = chatUrl(n);
  if (chat) return chat;

  const eventId = pickId(n.data, 'event_id');

  switch (n.type) {
    case 'event_application_pending':
      if (eventId) {
        return `/volunteers?eventId=${encodeURIComponent(eventId)}&tab=waiting`;
      }
      return '/volunteers?tab=waiting';
    case 'organization_membership_pending':
      return '/volunteers?orgTab=members&membersTab=requests';
    case 'volunteer_left_shift':
    case 'volunteer_changed_shift':
    case 'volunteer_joined_shift':
      // Straight to the shift board, which is where the freed spot gets filled.
      if (eventId) {
        return `/volunteers?eventId=${encodeURIComponent(eventId)}&tab=shifts`;
      }
      return '/volunteers?tab=shifts';
    case 'volunteer_left_event':
      if (eventId) {
        return `/volunteers?eventId=${encodeURIComponent(eventId)}`;
      }
      return '/volunteers';
    case 'task_submission_pending':
      if (eventId) {
        return `/volunteers?eventId=${encodeURIComponent(eventId)}&open=tasks&tasksTab=complete`;
      }
      return '/volunteers?open=tasks&tasksTab=complete';
    case 'hours_request_pending':
      if (eventId) {
        return `/volunteers?eventId=${encodeURIComponent(eventId)}&open=hourRequests`;
      }
      return '/volunteers?open=hourRequests';
    default:
      return null;
  }
}

/** @deprecated Use getNotificationNavigationUrl */
export function getVolunteerTasksUrlForNotification(n: {
  type: string;
  data?: Record<string, unknown> | null;
}): string | null {
  return volunteerTasksUrl(n);
}
